import { isEmpty, isNil } from "lodash";
import { CalendarMode, CalendarView, Overbooking, Row, Row as RowAsType } from "PFApp/booking/types";
import { ProfileWarningsIndex } from "PFCore/hooks/queries/profile";
import useBookingCategories from "PFCore/hooks/use_booking_categories";

import { RowType } from "../../../../../types/row";
import { getOriginalResource } from "../../../../helpers";
import { useBookingRowsContext } from "../../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../../context/bookings_overview_context";
import { HoverPeriodData } from "../row";
import { usePeriodAllowedToCreateBookingChecker } from "../use_period_allowed_to_create_booking_checker";

const getOverbookings = (
  row: Row,
  calendarMode: CalendarMode,
  warningsByProfile: ProfileWarningsIndex
): Overbooking[] => {
  const profileId = getOriginalResource(row, calendarMode)?.id;
  return profileId ? warningsByProfile?.[profileId]?.overbookings : [];
};

export const getRowProfileId = (
  calendarMode: CalendarMode,
  row: RowAsType,
  parentRow?: RowAsType
): number | undefined => {
  if (calendarMode === CalendarMode.Workforce) {
    return getOriginalResource(row.type !== RowType.Profile ? parentRow! : row, CalendarMode.Workforce)?.id;
  } else {
    return row.type === RowType.Profile ? getOriginalResource(row, CalendarMode.Projects)?.id : undefined;
  }
};

export const getRowActivityId = (
  calendarMode: CalendarMode,
  row: RowAsType,
  parentRow?: RowAsType
): number | undefined => {
  if ([RowType.Role, RowType.Engagement].includes(row.type)) {
    return getOriginalResource(row, calendarMode)?.id;
  } else {
    return calendarMode === CalendarMode.Workforce
      ? undefined
      : getOriginalResource(parentRow!, calendarMode)?.id;
  }
};

export const checkShouldDisplayOverbookings = (
  row: Row,
  calendarMode: CalendarMode,
  warningsByProfile: ProfileWarningsIndex
): boolean =>
  calendarMode === CalendarMode.Workforce &&
  row.type === RowType.Profile &&
  !isEmpty(getOverbookings(row, calendarMode, warningsByProfile));

export const checkShouldDisplayMisalignments = (row: Row, calendarMode: CalendarMode): boolean =>
  calendarMode === CalendarMode.Projects && row.type === RowType.Profile;

export const useShouldHoverPeriod = (
  row: Row,
  draggedBookingId: number | null,
  hoveredPeriod: HoverPeriodData["period"] | null
): boolean => {
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext();

  const { isEditModeEnabled } = useBookingRowsContext();
  const { bookingCategories } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategory = bookingCategories.some((i) => !i.readonly);
  const checkIsPeriodAllowedToCreateBooking = usePeriodAllowedToCreateBookingChecker();

  return (
    !isNil(hoveredPeriod) &&
    !draggedBookingId &&
    calendarMode === CalendarMode.Workforce &&
    (row.type !== RowType.Profile || [CalendarView.Compressed, CalendarView.BookingsOnly].includes(view)) &&
    hasWriteAccessToAtLeastOneCategory &&
    !isEditModeEnabled &&
    checkIsPeriodAllowedToCreateBooking(hoveredPeriod)
  );
};
